// Workbook (.xlsx/.xlsm) reading via exceljs — the npm-registry choice: the patched SheetJS
// builds are only distributed off-registry (the registry copy is CVE-frozen at 0.18.5).
// exceljs is imported lazily so its ~300KB-gzip browser bundle stays out of the eager chunk.

import type { Workbook } from "exceljs";

async function loadWorkbook(file: File): Promise<Workbook> {
  const { Workbook: ExcelWorkbook } = await import("exceljs");
  const workbook = new ExcelWorkbook();
  // exceljs types the argument as a Node Buffer, but the browser build accepts an
  // ArrayBuffer; cast via never to avoid referencing Node's Buffer type in browser code.
  await workbook.xlsx.load((await file.arrayBuffer()) as never);
  return workbook;
}

// Visible worksheet names in workbook order ("hidden"/"veryHidden" sheets are skipped).
export async function readWorkbookSheetNames(file: File): Promise<string[]> {
  const workbook = await loadWorkbook(file);
  return workbook.worksheets.filter((sheet) => (sheet.state ?? "visible") === "visible").map((sheet) => sheet.name);
}

export type SheetHeaderRequest = { sheetName: string; headerRow: number };

// Key for readWorkbookHeaders' result: one sheet's header at one row
export const buildSheetHeaderKey = (sheetName: string, headerRow: number) => JSON.stringify([sheetName, headerRow]);

// Header row of each requested sheet, in column order — cell.text flattens rich text,
// hyperlinks and cached formula results to their display string. Sheets that are missing
// or empty come back as an empty array rather than failing the whole read.
export async function readWorkbookHeaders(
  file: File,
  requests: SheetHeaderRequest[]
): Promise<Record<string, string[]>> {
  const workbook = await loadWorkbook(file);

  const result: Record<string, string[]> = {};
  for (const { sheetName, headerRow } of requests) {
    const sheet = workbook.getWorksheet(sheetName);
    const headers: string[] = [];
    sheet?.getRow(headerRow).eachCell({ includeEmpty: false }, (cell, colNumber) => {
      headers[colNumber - 1] = (cell.text ?? "").trim();
    });
    result[buildSheetHeaderKey(sheetName, headerRow)] = headers.filter(Boolean);
  }
  return result;
}
